import React, { memo, useCallback } from "react";

import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  VStack,
  Text,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { FieldValues, useForm } from "react-hook-form";
import { SERVER_ERROR } from "modules/utils/Constants";
import colors from "app/colors";
import {
  InternalAdminOrganizationEditMutation,
  InternalAdminOrganizationEditMutationVariables,
  InternalAdminOrganizationCreateMutation,
  InternalAdminOrganizationCreateMutationVariables,
} from "gql/graphql";
import { INTERNAL_ADMIN_ORGANIZATION_CREATE_MUTATION } from "../_networking/internal-admin-organization-create";
import { INTERNAL_ADMIN_ORGANIZATION_EDIT_MUTATION } from "../_networking/internal-admin-organization-edit";
import { getMessageFromError } from "modules/utils/Functions";
import { useOverlay } from "modules/providers/overlay-provider";
import useInternalAdminOrganizations from "../hooks/use-internal-admin-organizations";

interface IProps {
  organizationId?: string;
}

const InternalAdminOrganizationModal = ({ organizationId }: IProps) => {
  const { closeModal } = useOverlay();

  const { organization } = useInternalAdminOrganizations(organizationId);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
  } = useForm({
    mode: "onSubmit",
  });

  const [editOrganizationAPI] = useMutation<
    InternalAdminOrganizationEditMutation,
    InternalAdminOrganizationEditMutationVariables
  >(INTERNAL_ADMIN_ORGANIZATION_EDIT_MUTATION);

  const [createOrganizationAPI] = useMutation<
    InternalAdminOrganizationCreateMutation,
    InternalAdminOrganizationCreateMutationVariables
  >(INTERNAL_ADMIN_ORGANIZATION_CREATE_MUTATION);

  const onSubmit = useCallback(
    async ({ name, emailDomain, hubspotId }: FieldValues) => {
      try {
        if (organizationId) {
          await editOrganizationAPI({
            variables: {
              id: organizationId,
              input: {
                name,
                emailDomain,
                hubspotId,
              },
            },
          });
          closeModal();
        } else {
          await createOrganizationAPI({
            variables: {
              input: {
                name,
                emailDomain,
                hubspotId,
              },
            },
          });
        }
        closeModal();
      } catch (error: any) {
        setError(SERVER_ERROR, {
          message: error.message,
        });
      }
    },
    [
      organizationId,
      closeModal,
      editOrganizationAPI,
      createOrganizationAPI,
      setError,
    ],
  );

  return (
    <Modal isOpen onClose={closeModal} isCentered size="2xl">
      <ModalOverlay />
      <ModalContent borderRadius={16} w={1000}>
        <ModalHeader>
          <Center>
            <Text>
              {organizationId ? "Edit Organization" : "Add Organization"}
            </Text>
          </Center>
        </ModalHeader>
        <ModalBody p={6}>
          {organizationId && !organization ? (
            <Center>
              <Spinner size="xl" />
            </Center>
          ) : (
            <form id="edit-subject-form" onSubmit={handleSubmit(onSubmit)}>
              <VStack>
                <FormControl isInvalid={!!errors?.name}>
                  <FormLabel>Organization Name</FormLabel>
                  <Input
                    id="name"
                    defaultValue={organization ? organization.name : undefined}
                    placeholder="name"
                    {...register("name", {
                      required: "The name is required",
                      maxLength: {
                        value: 60,
                        message: "The name must be 60 characters or less",
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {getMessageFromError(errors?.name?.message)}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors?.emailDomain?.message}>
                  <FormLabel>
                    System Name (for subdomain e.g. acme.mysynco.com)
                  </FormLabel>
                  <Input
                    id="emailDomain"
                    defaultValue={
                      organization ? organization.emailDomain || "" : undefined
                    }
                    placeholder="emailDomain"
                    isReadOnly={!!organizationId}
                    {...register("emailDomain", {
                      required: "The emailDomain is required",
                      disabled: !!organizationId,
                      pattern: {
                        value: /^[a-zA-Z0-9._-]+\.mysynco\.com$/,
                        message:
                          "The emailDomain must contain only letters, numbers, dashes, and underscores and must end with .mysynco.com",
                      },
                      maxLength: {
                        value: 120,
                        message:
                          "The emailDomain must be 120 characters or less",
                      },
                    })}
                  />
                  <FormHelperText>
                    Special characters allowed: dashes and underscores
                  </FormHelperText>
                  <FormErrorMessage>
                    {getMessageFromError(errors?.emailDomain?.message)}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors?.hubspotId?.message}>
                  <FormLabel>Hubspot ID (optional)</FormLabel>
                  <Input
                    id="hubspotId"
                    defaultValue={
                      organization ? organization.hubspotId || "" : undefined
                    }
                    placeholder="hubspotId"
                    {...register("hubspotId", {
                      maxLength: {
                        value: 120,
                        message: "The hubspotId must be 120 characters or less",
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {getMessageFromError(errors?.hubspotId?.message)}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
              {errors.SERVER_ERROR && (
                <Text color={colors.red}>
                  {getMessageFromError(errors?.SERVER_ERROR?.message)}
                </Text>
              )}
              <Center pt={5}>
                <HStack>
                  <Button
                    variant="cancel"
                    height={8}
                    w={28}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeModal();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    height={8}
                    w={28}
                    type="submit"
                    onClick={() => {
                      clearErrors([SERVER_ERROR]);
                    }}
                  >
                    Save
                  </Button>
                </HStack>
              </Center>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminOrganizationModal);
